import React, { useState, useEffect, useCallback } from 'react';
import { searchSlots } from './BattleShared';

// ============================================
// THE BAN LIST — fun mugshot wall of banned slots/providers (route: /ban-list)
// Public: browse banned/unbanned slots + Most Wanted board.
// Admins: manage everything inline on this page (no admin panel tab).
// Mentioning a banned name in Twitch chat gets a snarky bot reply.
// ============================================

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// Mugshot height-chart backdrop
const MUGSHOT_BG = {
  background: 'repeating-linear-gradient(180deg, rgba(255,255,255,0.045) 0px, rgba(255,255,255,0.045) 1px, transparent 1px, transparent 22px), linear-gradient(180deg, rgba(30,30,38,0.9), rgba(12,12,16,0.95))'
};

const STAMP = (text, color) => ({
  position: 'absolute', top: '38%', left: '50%',
  transform: 'translate(-50%, -50%) rotate(-14deg)',
  border: `3px solid ${color}`, color, borderRadius: '6px',
  padding: '0.15rem 0.9rem', fontWeight: 900, letterSpacing: '4px',
  fontSize: '1.25rem', textTransform: 'uppercase',
  background: 'rgba(0,0,0,0.45)', pointerEvents: 'none',
  fontFamily: 'Outfit, sans-serif', whiteSpace: 'nowrap'
});

const ordinal = (n) => n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;

export default function BanListPage({ apiUrl, getToken, isAdmin, slotCatalog = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('banned'); // banned | wanted | unbanned
  const [message, setMessage] = useState(null);

  // Admin form state (add or edit)
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'slot', provider: '', imageUrl: '', reason: '', aliases: '' });
  const [saving, setSaving] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);

  const authHeaders = useCallback(() => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/banlist`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('Ban list load error:', e); }
    setLoading(false);
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 5000); };

  // Auto-fill image + provider from the slot picker catalog when the name matches
  const onNameChange = (name) => {
    setForm(f => {
      const match = slotCatalog.find(s => s.name.toLowerCase() === name.trim().toLowerCase());
      return {
        ...f, name,
        imageUrl: match && !f.imageUrl ? match.image : (match ? match.image : f.imageUrl),
        provider: match ? match.provider : f.provider
      };
    });
  };

  const openAdd = () => { setEditingId(null); setForm({ name: '', type: 'slot', provider: '', imageUrl: '', reason: '', aliases: '' }); setShowForm(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, type: s.type || 'slot', provider: s.provider || '', imageUrl: s.image_url || '', reason: s.reason || '', aliases: s.aliases || '' });
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url = editingId ? `${apiUrl}/api/admin/banlist/${editingId}` : `${apiUrl}/api/admin/banlist`;
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        flash('success', editingId ? 'Entry updated.' : `🔨 ${form.name} has been BANNED.`);
        setShowForm(false);
        load();
      } else {
        flash('error', body.error || 'Failed to save');
      }
    } catch (e) { flash('error', 'Failed to save'); }
    setSaving(false);
  };

  const unban = async (s) => {
    const reason = window.prompt(`Unban reason for ${s.name} (optional — e.g. "served its time"):`, '');
    if (reason === null) return;
    const res = await fetch(`${apiUrl}/api/admin/banlist/${s.id}/unban`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }) });
    if (res.ok) { flash('success', `🕊️ ${s.name} has been released.`); load(); }
    else { const b = await res.json().catch(() => ({})); flash('error', b.error || 'Failed to unban'); }
  };

  const reban = async (s) => {
    const reason = window.prompt(`Re-ban reason for ${s.name} (optional — leave blank to keep the old charge):`, '');
    if (reason === null) return;
    const res = await fetch(`${apiUrl}/api/admin/banlist/${s.id}/reban`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }) });
    if (res.ok) { flash('success', `🔨 ${s.name} is back behind bars.`); load(); }
    else { const b = await res.json().catch(() => ({})); flash('error', b.error || 'Failed to re-ban'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name} from the list entirely? Its whole rap sheet goes with it.`)) return;
    const res = await fetch(`${apiUrl}/api/admin/banlist/${s.id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { flash('success', 'Entry deleted.'); load(); }
    else flash('error', 'Failed to delete');
  };

  const toggleChat = async () => {
    const res = await fetch(`${apiUrl}/api/admin/banlist/chat-toggle`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ enabled: !data?.chatEnabled })
    });
    if (res.ok) load();
  };

  const banCount = (s) => s.events.filter(e => e.action === 'banned').length;

  const inputStyle = { padding: '0.55rem 0.85rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '9px', color: '#fff', fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif' };
  const tabBtn = (active) => ({ padding: '0.55rem 1.2rem', borderRadius: '10px', border: `1px solid ${active ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(239,68,68,0.14)' : 'rgba(255,255,255,0.03)', color: active ? '#fca5a5' : '#a1a1aa', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' });
  const smallBtn = (bg, border, color) => ({ padding: '0.3rem 0.65rem', background: bg, border: `1px solid ${border}`, borderRadius: '6px', color, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' });

  // Mugshot card (used for banned + unbanned tabs)
  const MugshotCard = ({ s, released }) => {
    const offenses = banCount(s);
    return (
      <div style={{
        position: 'relative', borderRadius: '14px', overflow: 'hidden',
        border: `1px solid ${released ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
        background: 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column'
      }}>
        {/* Mugshot photo area */}
        <div style={{ position: 'relative', aspectRatio: '4 / 3', ...MUGSHOT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {s.image_url ? (
            <img src={s.image_url} alt={s.name} loading="lazy" style={{
              width: '58%', maxHeight: '82%', objectFit: 'contain', borderRadius: '10px',
              filter: released ? 'none' : 'grayscale(0.85) contrast(1.05)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
            }} />
          ) : (
            <div style={{
              width: '58%', aspectRatio: '3 / 4', maxHeight: '82%', borderRadius: '10px',
              background: 'linear-gradient(160deg, rgba(168,85,247,0.25), rgba(0,0,0,0.6))',
              border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              filter: released ? 'none' : 'grayscale(0.5)'
            }}>
              <span style={{ fontSize: '2rem' }}>🎰</span>
              <span style={{ fontWeight: 900, fontSize: '1.3rem', color: 'rgba(255,255,255,0.85)', letterSpacing: '2px' }}>
                {s.name.split(/\s+/).slice(0, 3).map(w => w[0]).join('').toUpperCase()}
              </span>
            </div>
          )}
          <div style={STAMP(released ? 'RELEASED' : 'BANNED', released ? '#22c55e' : '#ef4444')}>{released ? 'RELEASED' : 'BANNED'}</div>
          {/* Inmate number plate */}
          <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '0.1rem 0.6rem', fontSize: '0.62rem', letterSpacing: '2px', color: '#d4d4d8', fontWeight: 700 }}>
            SNZ-{String(s.id).padStart(4, '0')}
          </div>
          {s.type === 'provider' && (
            <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(168,85,247,0.25)', border: '1px solid rgba(168,85,247,0.5)', borderRadius: '999px', padding: '0.12rem 0.55rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '1px', color: '#d8b4fe' }}>
              ENTIRE PROVIDER
            </div>
          )}
          {!released && s.mention_count > 0 && (
            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: '999px', padding: '0.12rem 0.55rem', fontSize: '0.64rem', fontWeight: 800, color: '#fbbf24' }}>
              📢 {s.mention_count.toLocaleString()}
            </div>
          )}
        </div>

        {/* Charge sheet */}
        <div style={{ padding: '0.9rem 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: '1rem', color: '#fff' }}>{s.name}</span>
            {s.provider && <span style={{ fontSize: '0.68rem', color: '#a78bfa', fontWeight: 600 }}>{s.provider}</span>}
          </div>
          {s.reason && (
            <div style={{ fontSize: '0.78rem', color: '#fbbf24', fontStyle: 'italic', lineHeight: 1.45 }}>
              <span style={{ fontWeight: 800, fontStyle: 'normal', color: '#f87171', letterSpacing: '1px', fontSize: '0.66rem' }}>CRIME: </span>
              {s.reason}
            </div>
          )}
          {released && s.unban_reason && (
            <div style={{ fontSize: '0.78rem', color: '#4ade80', fontStyle: 'italic', lineHeight: 1.45 }}>
              <span style={{ fontWeight: 800, fontStyle: 'normal', letterSpacing: '1px', fontSize: '0.66rem' }}>RELEASED: </span>
              {s.unban_reason}
            </div>
          )}
          {/* Rap sheet timeline */}
          <div style={{ fontSize: '0.68rem', color: '#71717a', lineHeight: 1.7, marginTop: 'auto' }}>
            {offenses > 1 && <span style={{ color: '#fca5a5', fontWeight: 800 }}>{ordinal(offenses)} OFFENSE · </span>}
            {s.events.map((e, i) => (
              <span key={i}>
                {i > 0 && ' → '}
                {e.action === 'banned' ? '🔨' : '🕊️'} {fmtDate(e.at)}
              </span>
            ))}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
              {released
                ? <button onClick={() => reban(s)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>🔨 Re-ban</button>
                : <button onClick={() => unban(s)} style={smallBtn('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.35)', '#4ade80')}>🕊️ Unban</button>}
              <button onClick={() => openEdit(s)} style={smallBtn('rgba(255,255,255,0.06)', 'rgba(255,255,255,0.15)', '#d4d4d8')}>✏️ Edit</button>
              <button onClick={() => remove(s)} style={smallBtn('rgba(239,68,68,0.08)', 'rgba(239,68,68,0.25)', '#f87171')}>Delete</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const banned = data?.banned || [];
  const unbanned = data?.unbanned || [];
  const wanted = data?.mostWanted || [];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
        <h1 className="page-title" style={{ letterSpacing: '2px' }}>🚫 THE BAN LIST</h1>
        <p className="page-subtitle">
          Slots that committed crimes against Snutzy's bankroll. Say their name in chat… see what happens.
        </p>
      </div>

      {message && (
        <div style={{
          padding: '0.75rem 1.1rem', borderRadius: '10px', marginBottom: '1.2rem', fontSize: '0.88rem', fontWeight: 600,
          background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
          color: message.type === 'success' ? '#4ade80' : '#fca5a5'
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs + admin controls */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={tabBtn(tab === 'banned')} onClick={() => setTab('banned')}>🔒 Banned ({banned.length})</button>
        <button style={tabBtn(tab === 'wanted')} onClick={() => setTab('wanted')}>🤠 Most Wanted</button>
        <button style={tabBtn(tab === 'unbanned')} onClick={() => setTab('unbanned')}>🕊️ Unbanned ({unbanned.length})</button>
        {isAdmin && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={toggleChat} style={smallBtn(
              data?.chatEnabled ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
              data?.chatEnabled ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.15)',
              data?.chatEnabled ? '#4ade80' : '#a1a1aa'
            )}>
              💬 Chat replies: {data?.chatEnabled ? 'ON' : 'OFF'}
            </button>
            <button onClick={openAdd} style={{ padding: '0.5rem 1.1rem', background: 'linear-gradient(135deg, #ef4444, #b91c1c)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>
              🔨 Ban a Slot
            </button>
          </div>
        )}
      </div>

      {/* Admin add/edit form */}
      {isAdmin && showForm && (
        <div style={{ padding: '1.1rem 1.3rem', borderRadius: '14px', background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.25)', marginBottom: '1.4rem' }}>
          <div style={{ fontWeight: 800, marginBottom: '0.8rem', color: '#fca5a5' }}>{editingId ? '✏️ Edit entry' : '🔨 Ban a slot or provider'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem', marginBottom: '0.6rem' }}>
            {(() => {
              // Custom autocomplete — only appears after 2+ typed characters (the
              // native <datalist> popped its full unstyled list on focus).
              const q = form.name.trim().toLowerCase();
              const suggestions = nameFocused && q.length >= 2
                ? searchSlots(slotCatalog, q, 8)
                : [];
              return (
                <div style={{ position: 'relative' }}>
                  <input
                    value={form.name}
                    onChange={e => onNameChange(e.target.value)}
                    onFocus={() => setNameFocused(true)}
                    onBlur={() => setTimeout(() => setNameFocused(false), 150)}
                    onKeyDown={e => { if (e.key === 'Escape') setNameFocused(false); }}
                    placeholder="Name (slot or provider)"
                    autoComplete="off"
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  />
                  {suggestions.length > 0 && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
                      background: 'rgba(14,12,20,0.98)', border: '1px solid rgba(168,85,247,0.35)',
                      borderRadius: '10px', overflow: 'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      {suggestions.map(s => (
                        <div
                          key={s.name}
                          onMouseDown={() => {
                            setForm(f => ({ ...f, name: s.name, provider: s.provider, imageUrl: s.image }));
                            setNameFocused(false);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.7rem', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.14)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {s.image
                            ? <img src={s.image} alt="" loading="lazy" style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                            : <span style={{ width: '28px', textAlign: 'center', flexShrink: 0 }}>🎰</span>}
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                          <span style={{ fontSize: '0.66rem', color: '#a78bfa', flexShrink: 0 }}>{s.provider}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={{ ...inputStyle, colorScheme: 'dark' }}>
              <option value="slot" style={{ background: '#18181b', color: '#fff' }}>Slot</option>
              <option value="provider" style={{ background: '#18181b', color: '#fff' }}>Entire provider</option>
            </select>
            <input value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} placeholder="Provider (auto-fills from catalog)" style={inputStyle} />
            <input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} placeholder="Image URL (auto-fills, or paste one)" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem', marginBottom: '0.8rem' }}>
            <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder='Crime (optional) — e.g. "0x on a $400 bonus"' style={inputStyle} />
            <input value={form.aliases} onChange={e => setForm(f => ({ ...f, aliases: e.target.value }))} placeholder='Chat aliases, comma-separated (e.g. "gates, olympus")' style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {form.imageUrl && <img src={form.imageUrl} alt="preview" style={{ height: '52px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }} />}
            <button onClick={submitForm} disabled={saving || !form.name.trim()} style={{ padding: '0.55rem 1.3rem', background: 'linear-gradient(135deg, #ef4444, #b91c1c)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: saving || !form.name.trim() ? 0.5 : 1 }}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'BAN IT'}
            </button>
            <button onClick={() => setShowForm(false)} style={smallBtn('rgba(255,255,255,0.05)', 'rgba(255,255,255,0.15)', '#a1a1aa')}>Cancel</button>
            <span style={{ fontSize: '0.7rem', color: '#71717a' }}>Chat matches the name + aliases (3+ characters, whole words).</span>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#71717a' }}>Opening the case files…</p>
      ) : (
        <>
          {/* BANNED tab */}
          {tab === 'banned' && (
            banned.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#71717a', padding: '2rem 0' }}>The cells are empty… for now. 🔓</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1.1rem' }}>
                {banned.map(s => <MugshotCard key={s.id} s={s} released={false} />)}
              </div>
            )
          )}

          {/* MOST WANTED tab */}
          {tab === 'wanted' && (
            wanted.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#71717a', padding: '2rem 0' }}>
                Nobody has dared say a banned name in chat yet. The board awaits. 🤠
              </p>
            ) : (
              <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
                  <div style={{ display: 'inline-block', border: '2px solid rgba(251,191,36,0.5)', borderRadius: '10px', padding: '0.5rem 1.6rem', background: 'rgba(251,191,36,0.06)' }}>
                    <div style={{ fontWeight: 900, letterSpacing: '5px', color: '#fbbf24', fontSize: '1.1rem' }}>MOST WANTED</div>
                    <div style={{ fontSize: '0.68rem', color: '#a1a1aa', letterSpacing: '1px' }}>CHAT'S MOST REQUESTED BANNED SLOTS · REWARD: NOTHING, IT'S BANNED</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {wanted.map((s, i) => (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.75rem 1rem',
                      borderRadius: '12px',
                      background: i === 0 ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${i === 0 ? 'rgba(251,191,36,0.45)' : i < 3 ? 'rgba(251,191,36,0.22)' : 'rgba(255,255,255,0.08)'}`
                    }}>
                      <span style={{ fontSize: i < 3 ? '1.4rem' : '0.95rem', fontWeight: 900, color: i < 3 ? '#fbbf24' : '#71717a', minWidth: '2.2rem', textAlign: 'center' }}>
                        {['🥇', '🥈', '🥉'][i] || `#${i + 1}`}
                      </span>
                      {s.image_url
                        ? <img src={s.image_url} alt={s.name} loading="lazy" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px', filter: 'grayscale(0.7)' }} />
                        : <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: 'rgba(168,85,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🎰</div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.92rem' }}>{s.name}</div>
                        {s.reason && <div style={{ fontSize: '0.7rem', color: '#a1a1aa', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.reason}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 900, color: '#fbbf24', fontSize: '1.05rem' }}>{s.mention_count.toLocaleString()}</div>
                        <div style={{ fontSize: '0.6rem', color: '#71717a', letterSpacing: '1px', fontWeight: 700 }}>MENTIONS</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#71717a', marginTop: '1rem' }}>
                  Every time someone says a banned name in Twitch chat, its counter goes up. Yes, chat farms this. That's the point.
                </p>
              </div>
            )
          )}

          {/* UNBANNED tab */}
          {tab === 'unbanned' && (
            unbanned.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#71717a', padding: '2rem 0' }}>No slot has ever earned its freedom. Brutal. ⛓️</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1.1rem' }}>
                {unbanned.map(s => <MugshotCard key={s.id} s={s} released={true} />)}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
